import { AgButtonCellRendererComponent } from './components/cell-renderers/button-cell-renderer/button-cell-renderer.component';
import { AgDateCellRendererComponent } from './components/cell-renderers/ag-date-cell-renderer/ag-date-cell-renderer.component';
import { AgSetFilterComponent } from './components/ag-set-filter/ag-set-filter.component';
import { AgNumberFilterComponent } from './components/ag-number-filter/ag-number-filter.component';
import { AgTextFloatingFilterComponent } from './components/ag-text-filter/ag-text-floating-filter.component';
import { AgNumberFloatingFilterComponent } from './components/ag-number-filter/ag-number-floating-filter.component';
import { AgTextFilterComponent } from './components/ag-text-filter/ag-text-filter.component';
import { AgDateRangeFilterComponent } from './components/ag-date-range-filter/ag-date-range-filter.component';
import { AgDownloadCellRendererComponent } from './components/cell-renderers/ag-download-cell-renderer/ag-download-cell-renderer.component';
import { AgGridModule } from 'ag-grid-angular';
import { AgGridRuleTitleComponent } from './components/cell-renderers/rule-title/rule-title.component';
import { AgGridRuleUsageComponent } from './components/cell-renderers/rule-usage/rule-usage.component';
import { AgGridRuleVolumeComponent } from './components/cell-renderers/rule-volume/rule-volume.component';
import { AgGridUserComponent } from './components/cell-renderers/user/user.component';
import { AgListCellRendererComponent } from './components/cell-renderers/ag-list-cell-renderer/ag-list-cell-renderer.component';
import { AgObjectCellRendererComponent } from './components/cell-renderers/ag-object-cell-renderer/ag-object-cell-renderer.component';
import { AgOutputFilesCellRendererComponent } from './components/cell-renderers/ag-output-files-cell-renderer/ag-output-files-cell-renderer.component';
import { AgObjectListCellRendererComponent } from './components/cell-renderers/ag-object-list-cell-renderer/ag-object-list-cell-renderer.component';
import { AgTimeCellRendererComponent } from './components/cell-renderers/ag-time-cell-renderer/ag-time-cell-renderer.component';
import { AgUserCellRendererComponent } from './components/cell-renderers/ag-user-cell-renderer/ag-user-cell-renderer.component';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FormsModule } from '@angular/forms';
import { DataGridModule } from './data-grid.module';
import { GridQuickSearchComponent } from './components/data-grid/grid-quick-search.component';
import { GridSelectionComponent } from './components/data-grid/grid-selection.component';
import { isDevMode, NgModule } from '@angular/core';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { ObjectTitleRendererComponent } from './components/cell-renderers/object-title-renderer/object-title-renderer.component';
import { SharedModule } from '../shared/shared.module';
import { AgBooleanCellRendererComponent } from './components/cell-renderers/ag-boolean-cell-renderer/ag-boolean-cell-renderer.component';
import { AgToggleCellRendererComponent } from './components/cell-renderers/ag-toggle-cell-renderer/ag-toggle-cell-renderer.component';

import {
  CellStyleModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  ColumnAutoSizeModule,
  CsvExportModule,
  CustomFilterModule,
  EventApiModule,
  GridStateModule,
  ModuleRegistry,
  NumberFilterModule,
  PaginationModule,
  provideGlobalGridOptions,
  RenderApiModule,
  RowApiModule,
  RowAutoHeightModule,
  RowSelectionModule,
  RowStyleModule,
  TextEditorModule,
  TextFilterModule,
  TooltipModule,
  ValidationModule,
} from 'ag-grid-community';
import {
  CellSelectionModule,
  ClipboardModule,
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  FiltersToolPanelModule,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  SetFilterModule,
} from 'ag-grid-enterprise';
import { AgDataObjectCellRendererComponent } from './components/cell-renderers/ag-data-object-cell-renderer/ag-data-object-cell-renderer.component';
import { AgActionsMenuCellRendererComponent } from './components/cell-renderers/ag-actions-menu-cell-renderer/ag-actions-menu-cell-renderer.component';
import { AgTypePillCellRendererComponent } from './components/cell-renderers/ag-type-pill-cell-renderer/ag-type-pill-cell-renderer.component';
import { AgMetricCellRendererComponent } from './components/cell-renderers/ag-metric-cell-renderer/ag-metric-cell-renderer.component';
import { AgBadgeCellRendererComponent } from './components/cell-renderers/ag-badge-cell-renderer/ag-badge-cell-renderer.component';
import { AgTagsCellRendererComponent } from './components/cell-renderers/ag-tags-cell-renderer/ag-tags-cell-renderer.component';
import { AgThumbnailCellRendererComponent } from './components/cell-renderers/ag-thumbnail-cell-renderer/ag-thumbnail-cell-renderer.component';
import { SaveAgGridSettingsDialogComponent } from './components/save-ag-grid-settings-dialog/save-ag-grid-settings-dialog.component';
import {
  SUI_DIALOG_SIMPLE_SLOTS,
  SUI_DIALOG_TABS_SLOTS,
  SuiDialogSimpleComponent,
  SuiDialogTabsComponent,
} from '@sui/dialog';
import { SuiButtonComponent } from '@sui/button';
import { SuiSelectComponent, SuiSelectOptionComponent } from '@sui/select';
import { SuiInputComponent } from '@sui/input';
import { SuiCheckboxComponent } from '@sui/checkbox';
import { SuiFormFieldComponent, SuiLabelDirective } from '@sui/form-field';
import { SuiBadgeComponent } from '@sui/badge';

// Only the ag-grid features the app uses (WAP-1714); a feature used without its
// module fails at runtime with a message naming the module - add it here.
// Sorting, column move / resize / groups and keyboard navigation are core.
ModuleRegistry.registerModules([
  // Row models: SSRM for app-data-grid and the remediation grids, client-side
  // for the two translation tables and Remediation Runs.
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  ClientSideRowModelModule,
  // Filters: provided text / number / set plus the Angular custom filters.
  TextFilterModule,
  NumberFilterModule,
  SetFilterModule,
  CustomFilterModule,
  // Toolbar-adjacent chrome: side panels, header menu, default context menu
  // on the standalone grids (the wrapper suppresses it).
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  ColumnMenuModule,
  ContextMenuModule,
  // Selection, range copy, paging, state, tooltips.
  RowSelectionModule,
  CellSelectionModule,
  ClipboardModule,
  PaginationModule,
  GridStateModule,
  TooltipModule,
  // Styling and layout hooks used by column defs.
  CellStyleModule,
  RowStyleModule,
  RowAutoHeightModule,
  ColumnAutoSizeModule,
  // Editing (default text editor) and export.
  TextEditorModule,
  CsvExportModule,
  // Grid API surface the wrapper and hosts call.
  ColumnApiModule,
  RenderApiModule,
  RowApiModule,
  EventApiModule,
  // Dev only: readable errors when a feature lacks its module.
  ...(isDevMode() ? [ValidationModule] : []),
]);

// Mark all grids as using the legacy theme
provideGlobalGridOptions({ theme: 'legacy' });
@NgModule({
  declarations: [
    GridQuickSearchComponent,
    GridSelectionComponent,
    AgGridUserComponent,
    AgGridRuleVolumeComponent,
    AgGridRuleTitleComponent,
    AgGridRuleUsageComponent,
    AgButtonCellRendererComponent,
    AgObjectListCellRendererComponent,
    AgUserCellRendererComponent,
    AgDateCellRendererComponent,
    AgTimeCellRendererComponent,
    AgOutputFilesCellRendererComponent,
    AgDownloadCellRendererComponent,
    ObjectTitleRendererComponent,
    AgListCellRendererComponent,
    AgDateRangeFilterComponent,
    AgTextFilterComponent,
    AgNumberFilterComponent,
    AgSetFilterComponent,
    AgTextFloatingFilterComponent,
    AgNumberFloatingFilterComponent,
    AgObjectCellRendererComponent,
    AgBooleanCellRendererComponent,
    AgToggleCellRendererComponent,
    AgDataObjectCellRendererComponent,
    AgActionsMenuCellRendererComponent,
    AgTypePillCellRendererComponent,
    AgMetricCellRendererComponent,
    AgBadgeCellRendererComponent,
    AgTagsCellRendererComponent,
    AgThumbnailCellRendererComponent,
    SaveAgGridSettingsDialogComponent,
  ],
  imports: [
    CommonModule,
    AgGridModule,
    DataGridModule,
    NgxChartsModule,
    FontAwesomeModule,
    SharedModule,
    FormsModule,

    SuiDialogTabsComponent,
    SuiDialogSimpleComponent,
    ...SUI_DIALOG_TABS_SLOTS,
    ...SUI_DIALOG_SIMPLE_SLOTS,
    SuiButtonComponent,
    SuiBadgeComponent,
    SuiSelectComponent,
    SuiSelectOptionComponent,
    SuiInputComponent,
    SuiCheckboxComponent,
    SuiFormFieldComponent,
    SuiLabelDirective,
  ],

  exports: [DataGridModule, GridQuickSearchComponent, GridSelectionComponent],
})
export class SensikaAgGridModule {}
